import { useCallback, useEffect, useRef, useState } from "react";
import type { z } from "zod";
import { eventFor } from "../domain/session";
import type { Session, SessionAction } from "../domain/session-schema";
import {
  DemoResponseSchema,
  SessionCreatedResponseSchema,
  SessionMutationResponseSchema,
  SessionResponseSchema,
} from "../worker/contracts";
import { ApiError, request } from "./api";
import { learnerProfileId } from "./learner-profile";
import { type LearnerCredentials, readSession, rememberSession } from "./session-store";

type Demo = z.infer<typeof DemoResponseSchema>;
type Turn = { readonly id: string; readonly question: string; readonly answer: string };

export function useClassroom() {
  const [demo, setDemo] = useState<Demo | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [pending, setPending] = useState(false);
  const [restoring, setRestoring] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<readonly Turn[]>([]);
  const credentials = useRef<LearnerCredentials | null>(null);
  const activeRequest = useRef(false);

  const loadDemo = useCallback(async () => {
    setError(null);
    try {
      const profile = learnerProfileId();
      setDemo(
        await request(
          profile ? `/api/profiles/${encodeURIComponent(profile)}` : "/api/demo",
          DemoResponseSchema,
        ),
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? "The classroom could not be opened. Please try again."
          : "The classroom is unavailable.",
      );
    }
  }, []);
  useEffect(() => {
    void loadDemo();
  }, [loadDemo]);
  useEffect(() => {
    let active = true;
    async function restore() {
      try {
        const saved = readSession();
        if (!saved) return;
        credentials.current = saved;
        const result = await request(`/api/sessions/${saved.id}`, SessionResponseSchema, {
          headers: { Authorization: `Bearer ${saved.token}` },
        });
        if (!active) return;
        setSession(result.session);
        if (result.session.status === "complete") rememberSession(null);
        if (result.session.question && result.session.answer)
          setHistory([
            {
              id: result.session.answer.id,
              question: result.session.question,
              answer: result.session.answer.text,
            },
          ]);
      } catch (cause) {
        if (!active) return;
        if (cause instanceof ApiError && cause.status === 401) {
          credentials.current = null;
          rememberSession(null);
        } else
          setError(
            cause instanceof Error
              ? "Could not restore your lesson. Try reconnecting when your connection returns."
              : "Session recovery is unavailable.",
          );
      } finally {
        if (active) setRestoring(false);
      }
    }
    void restore();
    return () => {
      active = false;
    };
  }, []);

  async function perform(work: () => Promise<void>) {
    if (activeRequest.current) return;
    activeRequest.current = true;
    setPending(true);
    setError(null);
    try {
      await work();
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        credentials.current = null;
        rememberSession(null);
      }
      setError(
        cause instanceof ApiError && cause.status === 401
          ? "This rehearsal has expired. Start a new rehearsal to continue."
          : cause instanceof Error
            ? "Could not update your lesson. Your confirmed position is preserved. Try reconnecting."
            : "The lesson could not be updated.",
      );
    } finally {
      activeRequest.current = false;
      setPending(false);
    }
  }

  async function send(current: Session, action: SessionAction) {
    const auth = credentials.current;
    if (!auth) throw new ApiError(401, "Session unavailable.");
    const result = await request(`/api/sessions/${auth.id}/events`, SessionMutationResponseSchema, {
      method: "POST",
      headers: { Authorization: `Bearer ${auth.token}` },
      body: JSON.stringify(eventFor(current, action)),
    });
    setSession(result.session);
    if (result.session.status === "complete") rememberSession(null);
    if (!result.accepted) throw new ApiError(409, "The session changed. Reconnect to continue.");
    return result.session;
  }

  async function start(mode: "hybrid" | "all-live") {
    await perform(async () => {
      if (session && session.status !== "complete" && credentials.current)
        await send(session, { type: "end" });
      const created = await request("/api/sessions", SessionCreatedResponseSchema, {
        method: "POST",
        body: JSON.stringify({ mode, runtime: "rehearsal" }),
      });
      credentials.current = {
        id: created.session.id,
        token: created.token,
        expiresAt: created.expiresAt,
      };
      rememberSession(credentials.current);
      setSession(created.session);
      setHistory([]);
      await send(created.session, { type: "start" });
    });
  }

  async function action(input: SessionAction) {
    if (session)
      await perform(async () => {
        const current =
          input.type === "resume" && session.status === "listening"
            ? await send(session, { type: "pause" })
            : session;
        await send(current, input);
      });
  }

  async function interrupt() {
    if (!session || session.status === "listening") return;
    await perform(async () => {
      const interrupted = await send(session, {
        type: "interrupt",
        onsetId: crypto.randomUUID(),
        inputSource: "ask",
        authorized: true,
      });
      await send(interrupted, { type: "stopped" });
    });
  }

  async function ask(question: string) {
    await perform(async () => {
      const auth = credentials.current;
      if (!auth) throw new ApiError(401, "Session unavailable.");
      const result = await request(
        `/api/sessions/${auth.id}/questions`,
        SessionMutationResponseSchema,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${auth.token}` },
          body: JSON.stringify({ question, idempotencyKey: crypto.randomUUID() }),
        },
      );
      setSession(result.session);
      if (!result.accepted) throw new ApiError(409, "Question unavailable.");
      const savedQuestion = result.session.question;
      const savedAnswer = result.session.answer;
      if (savedQuestion && savedAnswer)
        setHistory((turns) => [
          ...turns,
          {
            id: savedAnswer.id,
            question: savedQuestion,
            answer: savedAnswer.text,
          },
        ]);
    });
  }

  async function finishAnswer() {
    if (!session?.answer) return;
    const answerId = session.answer.id;
    await perform(async () => {
      const next = await send(session, {
        type: "answer-complete",
        answerId,
        playbackConfirmed: true,
      });
      if (next.status === "resuming") await send(next, { type: "resume" });
    });
  }

  async function reconnect() {
    await perform(async () => {
      const auth = credentials.current;
      if (!auth) return;
      const result = await request(`/api/sessions/${auth.id}`, SessionResponseSchema, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      setSession(result.session);
    });
  }

  return {
    demo,
    session,
    pending: pending || restoring,
    error,
    history,
    loadDemo,
    start,
    action,
    interrupt,
    ask,
    finishAnswer,
    reconnect,
  };
}
